import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from '@angular/material/dialog';
import { Constants } from 'src/app/app.constants';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { VoucherService } from 'src/app/services/voucher.service';
import { MerchantService } from 'src/app/services';
import { Customer } from 'src/app/models/customer';

interface RecipientType {
  value: string;
  label: string;
}

@Component({
  selector: 'app-issue-voucher-dialog',
  templateUrl: './issue-voucher-dialog.component.html',
  styleUrls: ['./issue-voucher-dialog.component.css'],
})
export class IssueVoucherDialogComponent implements OnInit {
  form!: FormGroup;
  voucherFormControl!: FormControl;
  recipientTypeFormControl!: FormControl;
  customerFormControl!: FormControl;
  emailFormControl!: FormControl;
  nameFormControl!: FormControl;
  quantityFormControl!: FormControl;

  saving = false;
  loading = false;
  vouchers: any[] = [];
  customers: Customer[] = [];
  vouchersLoading = false;
  customersLoading = false;
  notify = false;
  sendEmail = true;
  sendPushNotification = false;

  recipientTypeOptions: RecipientType[] = [
    { value: 'customer', label: 'Specific Customer' },
    { value: 'email', label: 'Email Recipient' },
    // { value: 'general', label: 'General/Charity Voucher' },
  ];

  quantityOptions = Array.from({ length: 10 }, (_, index) => index + 1).map(
    (el: number) => ({ number: el }),
  );

  constructor(
    public matDialogRef: MatDialogRef<IssueVoucherDialogComponent>,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private constants: Constants,
    private voucherService: VoucherService,
    private merchantService: MerchantService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.initializeFormControls();
    this.setupForm();
    this.getVouchers();
    this.getCustomers();
  }

  private initializeFormControls(): void {
    this.voucherFormControl = new FormControl('', [Validators.required]);
    this.recipientTypeFormControl = new FormControl(
      this.recipientTypeOptions[0],
      [Validators.required],
    );
    this.customerFormControl = new FormControl('');
    this.emailFormControl = new FormControl('', [Validators.email]);
    this.nameFormControl = new FormControl('');
    this.quantityFormControl = new FormControl({ number: 1 }, [
      Validators.required,
    ]);
  }

  private setupForm(): void {
    this.form = this.fb.group({
      voucher: this.voucherFormControl,
      recipientType: this.recipientTypeFormControl,
      customer: this.customerFormControl,
      email: this.emailFormControl,
      name: this.nameFormControl,
      quantity: this.quantityFormControl,
    });

    this.recipientTypeFormControl.valueChanges.subscribe((type) => {
      this.updateValidators(type);
    });
  }

  private updateValidators(recipientType: RecipientType): void {
    this.customerFormControl.clearValidators();
    this.emailFormControl.clearValidators();
    this.nameFormControl.clearValidators();

    if (recipientType.value === 'customer') {
      this.customerFormControl.setValidators([Validators.required]);
    } else if (recipientType.value === 'email') {
      this.emailFormControl.setValidators([
        Validators.required,
        Validators.email,
      ]);
      this.nameFormControl.setValidators([Validators.required]);
    } else {
      this.emailFormControl.setValidators([Validators.email]);
    }

    this.customerFormControl.updateValueAndValidity();
    this.emailFormControl.updateValueAndValidity();
    this.nameFormControl.updateValueAndValidity();
  }

  get isSpecificCustomer(): boolean {
    return this.recipientTypeFormControl.value?.value === 'customer';
  }

  get isEmailRecipient(): boolean {
    return this.recipientTypeFormControl.value?.value === 'email';
  }

  get isGeneralVoucher(): boolean {
    return this.recipientTypeFormControl.value?.value === 'general';
  }

  getVouchers(): void {
    this.vouchersLoading = true;
    this.voucherService
      .getVouchers(100, 0, true)
      .subscribe({
        next: (res: any) => {
          this.vouchers = res.docs ?? [];
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error?.message || 'Failed to load vouchers',
          );
        },
      })
      .add(() => (this.vouchersLoading = false));
  }

  getCustomers(): void {
    this.customersLoading = true;
    this.merchantService
      .getCustomers('_id', 'desc', 2000, 0, '', {})
      .subscribe({
        next: (res: any) => {
          this.customers = res.docs || [];

          this.customers.forEach((el) => {
            if (typeof el.user !== 'string') {
              el.email = el.user?.email ?? el?.email;
            }
          });

          this.customers.sort((a, b) => a.name!.localeCompare(b.name!));
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error?.message || 'Failed to load customers',
          );
        },
      })
      .add(() => (this.customersLoading = false));
  }

  hasVouchers(): void {
    if (this.vouchers.length === 0) {
      this.constants.snack(
        'No active vouchers available. Please create a voucher first.',
      );
    }
  }

  issueVoucher(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const voucher = this.voucherFormControl.value;
    const quantity = this.quantityFormControl.value.number;
    const recipientType = this.recipientTypeFormControl.value;

    let confirmationMessage = '';
    if (recipientType.value === 'customer') {
      const customer = this.customerFormControl.value;
      const notificationText = this.sendPushNotification
        ? ' They will receive an email and push notification.'
        : ' They will receive an email.';
      confirmationMessage = `Issue ${quantity} x ${voucher.displayAs} to ${customer.name}?${notificationText}`;
    } else if (recipientType.value === 'email') {
      const email = this.emailFormControl.value;
      const name = this.nameFormControl.value;
      const emailText = this.sendEmail
        ? ` An email will be sent to ${email} immediately.`
        : ` No email will be sent (you can send it manually later).`;
      confirmationMessage = `Issue ${quantity} x ${voucher.displayAs} to ${name}?${emailText}`;
    } else {
      const email = this.emailFormControl.value;
      const name = this.nameFormControl.value;
      if (email || name) {
        confirmationMessage = `Issue ${quantity} x ${voucher.displayAs} to ${
          name || email
        }?`;
      } else {
        confirmationMessage = `Issue ${quantity} x ${voucher.displayAs} as general voucher(s)?`;
      }
    }

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm Issue Voucher',
        body: confirmationMessage,
        buttonOne: 'Cancel',
        buttonTwo: 'Issue',
      },
      autoFocus: false,
      width: '400px',
      disableClose: true,
      panelClass: 'custom-dialog',
      scrollStrategy: new NoopScrollStrategy(),
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option === 1) {
        this.performIssueVoucher();
      }
    });
  }

  private performIssueVoucher(): void {
    this.saving = true;

    const voucher = this.voucherFormControl.value;
    const quantity = this.quantityFormControl.value.number;
    const recipientType = this.recipientTypeFormControl.value;
    const customer = this.customerFormControl.value;
    const email = this.emailFormControl.value;
    const name = this.nameFormControl.value;

    const payload: any = {
      voucherId: voucher._id,
      quantity: quantity,
    };

    if (recipientType.value === 'customer' && customer) {
      payload.customerId = customer._id;
      payload.shouldSendPushNotification = this.sendPushNotification;
    } else if (recipientType.value === 'email') {
      payload.recipientEmail = email.trim();
      payload.recipientName = name.trim();
      payload.sendEmail = this.sendEmail;
      payload.shouldSendPushNotification = false; // For email recipients, PN is automatic if they have the app
    } else if (recipientType.value === 'general') {
      if (email?.trim()) {
        payload.recipientEmail = email.trim();
      }
      if (name?.trim()) {
        payload.recipientName = name.trim();
      }
    }

    this.voucherService
      .issueVoucher(payload)
      .subscribe({
        next: (res: any) => {
          const message = res.message || 'Voucher issued successfully!';
          this.constants.snack(message);
          this.matDialogRef.close(true);
        },
        error: (error: HttpErrorResponse) => {
          this.constants.snack(
            error.error?.message || 'Failed to issue voucher',
          );
        },
      })
      .add(() => (this.saving = false));
  }
}
